import { useState, useEffect, useRef } from 'react'
import { Plus, Trash2, Hash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { leadsAPI } from '../pages/Leads/api'

interface Props {
  onToast: (message: string, type: 'success' | 'error') => void
}

export default function MineNaceKoderSection({ onToast }: Props) {
  const { t } = useTranslation('components')
  const [saved, setSaved] = useState<{ id: number; nace_kode: string; nace_beskrivelse?: string | null }[]>([])
  const [loading, setLoading] = useState(true)

  // Search-based add
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<{ nace_kode: string; nace_beskrivelse: string | null }[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [searching, setSearching] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Batch add
  const [batchInput, setBatchInput] = useState('')

  useEffect(() => {
    setLoading(true)
    leadsAPI.getMineNaceKoder()
      .then(res => setSaved(res.data))
      .catch(() => onToast(t('mineNaceKoder.loadError'), 'error'))
      .finally(() => setLoading(false))
  }, [])

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([])
      return
    }
    const timer = setTimeout(() => {
      setSearching(true)
      leadsAPI.searchNaceCodes(searchQuery, 20)
        .then(res => {
          const savedSet = new Set(saved.map(s => s.nace_kode))
          setSearchResults(res.data.filter(r => !savedSet.has(r.nace_kode)))
          setShowDropdown(true)
        })
        .catch(() => {})
        .finally(() => setSearching(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, saved])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function handleAddOne(nace_kode: string, nace_beskrivelse?: string | null) {
    try {
      await leadsAPI.addMineNaceKode(nace_kode)
      setSaved(prev => [...prev, { id: 0, nace_kode, nace_beskrivelse }])
      setSearchQuery('')
      setShowDropdown(false)
      onToast(t('mineNaceKoder.added', { code: nace_kode }), 'success')
    } catch {
      onToast(t('mineNaceKoder.addError'), 'error')
    }
  }

  async function handleBatchAdd() {
    const input = batchInput.trim()
    if (!input) return
    try {
      const res = await leadsAPI.addMineNaceKoderBatch(input)
      const codes = input.split(',').map(c => c.trim()).filter(Boolean)
      const newItems = codes.map(c => ({ id: 0, nace_kode: c }))
      setSaved(prev => {
        const existingSet = new Set(prev.map(s => s.nace_kode))
        return [...prev, ...newItems.filter(n => !existingSet.has(n.nace_kode))]
      })
      setBatchInput('')
      onToast(t('mineNaceKoder.batchAdded', { n: res.data.added }), 'success')
    } catch {
      onToast(t('mineNaceKoder.batchAddError'), 'error')
    }
  }

  async function handleRemove(nace_kode: string) {
    try {
      await leadsAPI.deleteMineNaceKode(nace_kode)
      setSaved(prev => prev.filter(s => s.nace_kode !== nace_kode))
      onToast(t('mineNaceKoder.removed'), 'success')
    } catch {
      onToast(t('mineNaceKoder.removeError'), 'error')
    }
  }

  return (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <Hash className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('mineNaceKoder.title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('mineNaceKoder.subtitle')}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse h-20 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : (
        <>
          {/* Search add */}
          <div className="relative mb-3" ref={dropdownRef}>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                placeholder={t('mineNaceKoder.searchPlaceholder')}
                className="input-field text-sm flex-1"
              />
            </div>
            {showDropdown && searchResults.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map(r => (
                  <li key={r.nace_kode}>
                    <button
                      onClick={() => handleAddOne(r.nace_kode, r.nace_beskrivelse)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-3 h-3 text-primary-600 shrink-0" />
                      <span className="font-mono font-medium">{r.nace_kode}</span>
                      <span className="text-gray-500 truncate">{r.nace_beskrivelse || ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {searching && (
              <div className="absolute right-3 top-2.5 text-xs text-gray-400">{t('mineNaceKoder.searching')}</div>
            )}
          </div>

          {/* Batch add */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={batchInput}
              onChange={e => setBatchInput(e.target.value)}
              placeholder={t('mineNaceKoder.batchPlaceholder')}
              className="input-field text-sm flex-1"
              onKeyDown={e => e.key === 'Enter' && handleBatchAdd()}
            />
            <button onClick={handleBatchAdd} disabled={!batchInput.trim()} className="btn-primary flex items-center gap-1 text-sm">
              <Plus className="w-4 h-4" /> {t('mineNaceKoder.add')}
            </button>
          </div>

          {saved.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('mineNaceKoder.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {saved.map(s => (
                <li key={s.nace_kode} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/30">
                  <span className="text-sm">
                    <span className="font-mono font-medium">{s.nace_kode}</span>
                    {s.nace_beskrivelse && (
                      <span className="text-gray-500 ml-2">&mdash; {s.nace_beskrivelse}</span>
                    )}
                  </span>
                  <button onClick={() => handleRemove(s.nace_kode)}
                    className="text-red-500 hover:text-red-700 transition-colors p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
